import { z } from 'zod';

/**
 * Deployment-wide product feedback is backed by a Canny team API key. The
 * secret keys keep their historical `CANNY_*` spelling: they are the literal
 * names operators have already stored, and renaming them would silently
 * un-configure every existing deployment.
 */

/** Service-tier Canny API key used for in-app product feedback. */
export const CANNY_API_KEY = 'CANNY_API_KEY';
/** First (default) board id new product-feedback posts are created on. */
export const CANNY_BOARD_ID = 'CANNY_BOARD_ID';
/** Display name for the default platform feedback board. */
export const CANNY_BOARD_NAME = 'CANNY_BOARD_NAME';
/** JSON list of `{ boardId, boardName }` boards the Send feedback popover can target. */
export const CANNY_FEEDBACK_BOARDS = 'CANNY_FEEDBACK_BOARDS';
/** Board id new session Report bug posts are created on. */
export const CANNY_BUG_BOARD_ID = 'CANNY_BUG_BOARD_ID';
/** Display name for the platform bug board. */
export const CANNY_BUG_BOARD_NAME = 'CANNY_BUG_BOARD_NAME';

export const FEEDBACK_NOT_CONFIGURED = 'feedback_not_configured';

export const FEEDBACK_NOT_CONFIGURED_MESSAGE =
  'Product feedback is not configured on this deployment.';

export const BUG_REPORTING_NOT_CONFIGURED_MESSAGE =
  'Bug reporting is not configured on this deployment.';

/** Secrets that must be set before Send feedback is shown in the app. */
export const FEEDBACK_SECRET_KEYS = [CANNY_API_KEY, CANNY_BOARD_ID] as const;
/** Secrets that must be set before Report bug is shown on sessions. */
export const BUG_SECRET_KEYS = [CANNY_API_KEY, CANNY_BUG_BOARD_ID] as const;

export const FeedbackBoardSchema = z.object({
  boardId: z.string().min(1),
  boardName: z.string().min(1),
});
export type FeedbackBoard = z.infer<typeof FeedbackBoardSchema>;

const FeedbackBoardsJsonSchema = z.array(FeedbackBoardSchema).min(1);

/** Read the curated Send feedback boards, falling back to the single default board secret. */
export function parseFeedbackBoards(input: {
  boardsJson?: string | null;
  boardId?: string | null;
  boardName?: string | null;
}): FeedbackBoard[] {
  const raw = input.boardsJson?.trim() ?? '';
  if (raw.length > 0) {
    try {
      const parsed = FeedbackBoardsJsonSchema.safeParse(JSON.parse(raw) as unknown);
      if (parsed.success) return parsed.data;
    } catch {
      // Fall through to the single-board secrets.
    }
  }
  const boardId = input.boardId?.trim() ?? '';
  if (!boardId) return [];
  const boardName = input.boardName?.trim() || boardId;
  return [{ boardId, boardName }];
}

/** Honor a client board id only when it is one of the configured Send feedback boards. */
export function resolveFeedbackBoard(
  boards: readonly FeedbackBoard[],
  requestedBoardId?: string | null,
): FeedbackBoard | null {
  if (requestedBoardId) {
    const match = boards.find((board) => board.boardId === requestedBoardId);
    if (match) return match;
  }
  return boards[0] ?? null;
}
